import * as angular from 'angular';
import {IController} from 'angular';
import * as _ from 'lodash';
import {BatchRequestsService} from '../../../batch/services/batch-requests.service';
import {BatchDialogService} from '../../../batch/services/batch-dialogs-service.service';
import {BringozLoaderService} from '../../../commons/services/bringoz-loader.service';
import {ConfirmDialogService} from '../../../commons/services/confirm-dialog.service';
import {ConfirmChangeStateService} from '../../../commons/services/confirm-change-state.service';
import {PoliciesAlertsService} from '../../services/policies-alerts.service';
import {PoliciesDialogService} from '../../services/policies-dialogs-service.service';
import {ADMIN_USERS_IMPORT_CONFIG, ADMIN_USERS_IMPORT_MAX_ERRORS} from '../../constants/admin-users-import.const';
import {
  AdminUserImportCategoryTotals,
  AdminUserImportRow,
  buildCellErrors,
  buildDisplay,
  countRowsByCategory,
  isDuplicateRow,
  normalizeErrors
} from '../../utils/admin-user-import.util';

type AdminUsersImportNavItem = 'total' | 'valid' | 'errors' | 'warnings' | 'duplicates';

export class AdminUsersImportViewCtrl implements IController {

  siteId: string;

  public importConfig = ADMIN_USERS_IMPORT_CONFIG;
  public maxErrors: number = ADMIN_USERS_IMPORT_MAX_ERRORS;

  public rows: AdminUserImportRow[] = [];
  public visibleRows: AdminUserImportRow[] = [];
  public queryString: string;
  public currentNavItem: AdminUsersImportNavItem = 'total';

  public totals = {total: 0, valid: 0, errors: 0, warnings: 0, duplicates: 0};
  public categoryTotals: AdminUserImportCategoryTotals;
  public abortedSummary: {total: number, errors: number, categoryTotals: AdminUserImportCategoryTotals};

  public uploadComplete: boolean = false;
  public isAborted: boolean = false;
  public isLoading: boolean = false;
  public isFieldMappingOpen: boolean = false;
  public hasMappingFields: boolean = true;

  public schemas: any[] = [];
  public schemaDefaultFields: any[] = [];

  /* @ngInject */
  constructor(
    private $state,
    private $log,
    private $q,
    private UUIDService,
    private policiesDialogService: PoliciesDialogService,
    private batchRequestsService: BatchRequestsService,
    private batchDialogService: BatchDialogService,
    private bringozLoaderService: BringozLoaderService,
    private confirmDialogService: ConfirmDialogService,
    private confirmChangeStateService: ConfirmChangeStateService,
    private policiesAlertsService: PoliciesAlertsService
  ) {
  }

  $onInit() {
    this.categoryTotals = countRowsByCategory([]);
    this._loadSchemas();
  }

  $onDestroy() {
    // The unsaved changes guard is global, leaving it enabled would prompt on unrelated screens.
    this.confirmChangeStateService.reset();
  }

  changeNavItem(item: AdminUsersImportNavItem) {
    this.currentNavItem = item;
    this._applyVisibleRows();
  }

  submitQuery(query: string) {
    this.queryString = query;
    this._applyVisibleRows();
  }

  onUpload(event: Event) {
    this.policiesDialogService.showUploadAdminUsersDialog(event, this.schemas)
      .then((rows) => {
        // The validation screen is rebuilt from scratch on every upload attempt, not only the first.
        this._reset();
        this._applyRows(_.map(rows || [], (row) => this._enrichRow(row)));
        this.confirmChangeStateService.setIsEnabled(this.uploadComplete);
      }, () => {
      });
  }

  onRemoveRow(row: AdminUserImportRow) {
    this._applyRows(_.reject(this.rows, (r) => r.rowId === row.rowId));

    if (!this.rows.length) {
      this._reset();
    }
  }

  onRemoveInvalidRows(event: Event) {
    return this.confirmDialogService
      .confirm('Remove All Invalid Rows', 'Are you sure you want to remove all the rows with errors?', event)
      .then(() => {
        this._applyRows(_.reject(this.rows, (row) => !!row.hasErrors));

        if (!this.rows.length) {
          this._reset();
        }
      })
      .catch(() => {
        this.$log.info('Dialog Closed');
      });
  }

  onRemoveDuplicateRows(event: Event) {
    return this.confirmDialogService
      .confirm('Remove Duplicates', 'Are you sure you want to remove all the duplicate rows?', event)
      .then(() => {
        this._applyRows(_.reject(this.rows, (row) => !!row.isDuplicate));

        if (!this.rows.length) {
          this._reset();
        }
      })
      .catch(() => {
        this.$log.info('Dialog Closed');
      });
  }

  onCancel(event: Event) {
    return this.confirmDialogService
      .confirm('Cancel Upload', 'Are you sure you want to cancel the upload?', event)
      .then(() => {
        this._reset();
      })
      .catch(() => {
        this.$log.info('Dialog Closed');
      });
  }

  onImport(event: Event) {
    // Rows the server flagged cannot be imported, so only clean rows are sent. Picking the server
    // keys explicitly keeps every client only field (display, cellErrors, ...) out of the payload.
    const items = _.chain(this.rows)
      .reject((row) => row.hasErrors)
      .map((row) => _.pick(row, ['rowNumber', 'entity', 'mappingErrors']))
      .value();

    if (!items.length) {
      return;
    }

    this.bringozLoaderService.on();

    this.batchRequestsService.createAgentAssignmentsBatch(this.importConfig.importerPath, items)
      .then((batch) => {
        this.bringozLoaderService.off();
        return this.batchDialogService.batchAgentAssignmentsProgress(
          event, batch, this.importConfig.importerPath, this.importConfig.label
        );
      })
      .then((errorItems: any) => {
        this._applyBatchErrors(errorItems || []);
      })
      .catch((error) => {
        if (error) {
          this.policiesAlertsService.importAdminUsersError(error);
          this.$log.error('Failed to import admin users', error);
        } else {
          this.$log.info('Dialog Closed');
        }
      })
      .finally(() => {
        this.bringozLoaderService.off();
      });
  }

  onBackToUsers() {
    this.confirmChangeStateService.reset();
    this.$state.go('policy.users');
  }

  onOpenFieldMappingPanel() {
    this.isFieldMappingOpen = true;
  }

  onCloseFieldMappingPanel() {
    this.isFieldMappingOpen = false;
  }

  onSchemaCreated(schema) {
    this.schemas = [...this.schemas, schema];
  }

  onSchemaUpdated(schema) {
    this.schemas = _.map(this.schemas, (s) => s.id === schema.id ? schema : s);
  }

  onSchemaDeleted(schemaId) {
    this.schemas = _.reject(this.schemas, (s) => s.id === schemaId);
  }

  // ******************************
  //        Private methods
  // ******************************

  private _loadSchemas() {
    this.isLoading = true;

    // batchRequestsService returns native promises - $q.all adopts them so the results land inside a
    // digest. A failure must not block uploading, it only means no saved mapping can be picked,
    // hence the per-call catch.
    this.$q.all([
      this.$q.when(this.batchRequestsService.getSchemas(this.importConfig.entityType)).catch(() => []),
      this.$q.when(this.batchRequestsService.getSchemaDefaultFields(this.importConfig.entityType)).catch(() => [])
    ])
      .then(([schemas, schemaDefaultFields]: any[]) => {
        this.schemas = schemas || [];
        this.schemaDefaultFields = schemaDefaultFields || [];

        // An empty field list almost always means the entity type does not match the backend.
        this.hasMappingFields = !!this.schemaDefaultFields.length;
        if (!this.hasMappingFields) {
          this.$log.warn('No import mapping fields returned for entity type', this.importConfig.entityType);
        }
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  private _applyRows(rows: AdminUserImportRow[]) {
    const errorRowsCount = _.filter(rows, (row) => row.hasErrors).length;

    if (errorRowsCount > this.maxErrors) {
      this._abort(rows, errorRowsCount);
      return;
    }

    this.rows = rows;
    this.isAborted = false;
    this.abortedSummary = undefined;
    this.totals = this._getTotals(rows);
    this.categoryTotals = countRowsByCategory(rows);
    this.uploadComplete = !!rows.length;
    this._applyVisibleRows();
  }

  private _applyVisibleRows() {
    const query = _.toLower(_.trim(this.queryString || ''));

    this.visibleRows = _.chain(this.rows)
      .filter((row) => {
        if (this.currentNavItem === 'valid') {
          return !row.hasErrors;
        }
        if (this.currentNavItem === 'errors') {
          return row.hasErrors;
        }
        if (this.currentNavItem === 'warnings') {
          return row.hasWarnings;
        }
        if (this.currentNavItem === 'duplicates') {
          return row.isDuplicate;
        }
        return true;
      })
      .filter((row) => !query || _.includes(row.searchText, query))
      .value();
  }

  private _enrichRow(row: AdminUserImportRow): AdminUserImportRow {
    const errors = normalizeErrors(row.mappingErrors || []);
    const display = buildDisplay(row.entity || row.rawRow);

    return {
      ...row,
      rowId: this.UUIDService.generate(),
      errors: errors,
      cellErrors: buildCellErrors(errors),
      categories: _.uniq(_.map(errors, 'category')),
      hasErrors: _.some(errors, (error) => error.isBlocking),
      hasWarnings: _.some(errors, (error) => !error.isBlocking),
      isDuplicate: isDuplicateRow(errors),
      display: display,
      searchText: _.toLower(_.values(display).join(' '))
    };
  }

  private _getTotals(rows: AdminUserImportRow[]) {
    return {
      total: rows.length,
      valid: _.filter(rows, (row) => !row.hasErrors).length,
      errors: _.filter(rows, (row) => row.hasErrors).length,
      warnings: _.filter(rows, (row) => row.hasWarnings).length,
      duplicates: _.filter(rows, (row) => row.isDuplicate).length
    };
  }

  private _applyBatchErrors(errorItems: any[]) {
    if (!errorItems.length) {
      this.policiesAlertsService.importAdminUsersSuccess();
      this._reset();
      this.onBackToUsers();
      return;
    }

    const errorRowNumbers = _.map(errorItems, 'rowNumber');

    const failedRows = _.chain(this.rows)
      .filter((row) => _.includes(errorRowNumbers, row.rowNumber))
      .map((row) => {
        const errorItem = _.find(errorItems, (item) => item.rowNumber === row.rowNumber);
        return this._enrichRow({
          ...row,
          mappingErrors: _.get(errorItem, 'mappingErrors', [])
        });
      })
      .value();

    // The batch progress dialog resolves row numbers from task metadata. If the importer does not
    // report them the failures cannot be matched back to rows - keep the current rows on screen
    // instead of silently dropping the user back to the empty upload state.
    if (!failedRows.length) {
      this.policiesAlertsService.importAdminUsersError();
      this.$log.error('Import reported failures that could not be matched to uploaded rows', errorItems);
      return;
    }

    this.currentNavItem = 'errors';
    this._applyRows(failedRows);
  }

  private _abort(rows: AdminUserImportRow[], errorRowsCount: number) {
    this.abortedSummary = {
      total: rows.length,
      errors: errorRowsCount,
      categoryTotals: countRowsByCategory(rows)
    };

    // The rows are dropped on purpose - an aborted file is never reviewed or imported, and keeping
    // thousands of enriched rows around only costs memory and digest time.
    this.rows = [];
    this.visibleRows = [];
    this.totals = this._getTotals([]);
    this.categoryTotals = countRowsByCategory([]);
    this.uploadComplete = false;
    this.isAborted = true;
    this.confirmChangeStateService.reset();
  }

  private _reset() {
    this.currentNavItem = 'total';
    this.queryString = undefined;
    this.isAborted = false;
    this.abortedSummary = undefined;
    this._applyRows([]);
    this.confirmChangeStateService.reset();
  }
}

angular
  .module('bringoz.admin.policies')
  .component('adminUsersImportView', {
    bindings: {
      siteId: '<?'
    },
    template: require('./admin-users-import-view.html').default,
    controller: AdminUsersImportViewCtrl
  });
